
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useBusinessTypeFeatures } from "@/hooks/useBusinessTypeFeatures";
import { useQueryClient } from "@tanstack/react-query";

export const useResetData = (onClose: () => void) => {
  const [isLoading, setIsLoading] = useState(false);
  const { hasFeatureAccess } = useBusinessTypeFeatures();
  const hasPoultryFeature = hasFeatureAccess("poultry");
  // Utiliser le queryClient pour invalider les caches
  const queryClient = useQueryClient();

  const handleReset = async () => {
    try {
      setIsLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return;
      }

      // 1. Delete egg productions first (no foreign key dependencies)
      if (hasPoultryFeature) {
        await supabase
          .from('egg_productions')
          .delete()
          .eq('created_by', user.id);
      }

      // 2. Delete credit payments (depends on poultry_transactions)
      const { data: transactions } = await supabase
        .from('poultry_transactions')
        .select('id')
        .eq('created_by', user.id);

      if (transactions && transactions.length > 0) {
        const transactionIds = transactions.map(t => t.id);
        await supabase
          .from('credit_payments')
          .delete()
          .in('transaction_id', transactionIds);
      }

      // 3. Delete cart items
      await supabase
        .from('cart_items')
        .delete()
        .eq('created_by', user.id);

      // 4. Delete stock losses
      await supabase
        .from('stock_losses')
        .delete()
        .eq('created_by', user.id);

      // 5. Delete counting history and discrepancies
      await supabase
        .from('counting_history')
        .delete()
        .eq('owner_id', user.id);

      await supabase
        .from('counting_discrepancies')
        .delete()
        .eq('created_by', user.id);

      // 6. Delete all transactions
      await supabase
        .from('poultry_transactions')
        .delete()
        .eq('created_by', user.id);

      // 7. Delete access controls - Reset tab access codes
      await supabase
        .from('access_controls')
        .delete()
        .eq('owner_id', user.id);

      // 8. Delete validated amounts
      await supabase
        .from('validated_amounts')
        .delete()
        .eq('owner_id', user.id);

      // 9. Get the business stock ID
      const { data: ownerData } = await supabase
        .from('owner_data')
        .select('business_type_id')
        .eq('owner_id', user.id)
        .single();

      if (ownerData) {
        // 10. Get all business stocks for this owner
        const { data: businessStocks } = await supabase
          .from('business_stocks')
          .select('id')
          .eq('business_id', ownerData.business_type_id)
          .eq('owner_id', user.id);

        if (businessStocks && businessStocks.length > 0) {
          const stockIds = businessStocks.map(stock => stock.id);
          
          // 11. Delete all stock items for these business stocks
          await supabase
            .from('stock_items')
            .delete()
            .in('stock_id', stockIds);

          console.log("Stock items deleted successfully");
        }
      }

      // Invalider tous les caches qui pourraient contenir des données maintenant supprimées
      await queryClient.invalidateQueries({ queryKey: ['stockItems'] });
      await queryClient.invalidateQueries({ queryKey: ['transactions'] });
      await queryClient.invalidateQueries({ queryKey: ['validatedAmounts'] });
      await queryClient.invalidateQueries({ queryKey: ['countingHistory'] });
      await queryClient.invalidateQueries({ queryKey: ['eggProductions'] });
      
      toast.success("Toutes les données ont été réinitialisées avec succès");
      onClose();
      
      // Actualiser la page pour garantir que tous les composants sont mis à jour
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error("Erreur lors de la réinitialisation:", error);
      toast.error("Une erreur est survenue lors de la réinitialisation");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleReset
  };
};
